import { useMemo } from 'react';
import { Alert, Box, Card, Heading, Icon, Paragraph } from '@zillow/constellation-10';
import { IconFileFilled } from '@zillow/constellation-icons';
import { formatRequirement, REQUIRED_DOCUMENT_COPY } from '@zg-rentals/applications-components';
import type { Answers, Document } from '../../../../models/rental-application';
import type { FeatureFlags } from '../../../../contexts/ApplicationContext';
import { useApplicationContext } from '../../../../contexts/ApplicationContext';
import { useIncomeDocsRedaction } from '../../../../hooks/useIncomeDocsRedaction';
import type { IncomeSourceGroup, RequestedRow } from '../../../../utils/incomeDocuments';
import { getIncomeDocumentsModel } from '../../../../utils/incomeDocuments';
import { DOCUMENTS_EXPIRED_TEXT, INCOME_DOC_ENCOURAGEMENT_TEXT } from '../../../../constants/incomeDocs';
import {
  ADDITIONAL_FINANCIAL_DOCUMENTS_HEADING,
  DEFAULT_MODE_INTRO,
  DEFAULT_REQUESTER_NAME,
  INCOME_DOCUMENTS_STEP_HEADING,
  INCOME_SOURCE_DOCUMENTS_HEADING,
  NO_INCOME_SOURCES_TEXT,
  NO_LONGER_REQUESTED_HEADING,
  NO_LONGER_REQUESTED_TEXT,
  OPTIONAL_TAG,
  PASSWORD_PROTECTED_TEXT,
  REDACTION_TEXT,
  STEP_TIP_HEADING,
  STEP_TIP_TEXT,
  SUPPORTING_DOCUMENTS_HEADING,
  templateModeIntro,
} from '../../../../constants/incomeDocuments';
import { DocumentContainer } from './IncomeDocuments/DocumentContainer';
import { DocumentGroup } from './IncomeDocuments/DocumentGroup';
import { RequirementsProgress } from './IncomeDocuments/RequirementsProgress';

function useDocumentDetails(documents: Array<Document>) {
  const { documents: details, areDocumentsExpired } = useIncomeDocsRedaction(documents);
  const withDetails = (rowDocuments: Array<Document>) =>
    rowDocuments.map((document) => details?.find((detail) => detail.id === document.id) ?? document);

  return { withDetails, areDocumentsExpired };
}

function RequestedRowContainer({
  row,
  tag,
  isRequested,
  documents,
}: {
  row: RequestedRow;
  tag?: string;
  isRequested: boolean;
  documents: Array<Document>;
}) {
  const requirement = formatRequirement(
    row.section.parameterQuantity ?? undefined,
    row.section.parameterUnit ?? undefined,
  );

  return (
    <DocumentContainer
      heading={REQUIRED_DOCUMENT_COPY[row.documentType].label}
      tag={tag}
      requirement={requirement || undefined}
      isRequested={isRequested}
      documents={documents}
    />
  );
}

function IncomeSourceDocuments({ group, isTemplate }: { group: IncomeSourceGroup; isTemplate: boolean }) {
  const { withDetails, areDocumentsExpired } = useDocumentDetails([
    ...group.requestedRows.flatMap((row) => row.documents),
    ...group.supportingDocuments,
  ]);

  return (
    <DocumentGroup
      title={group.label}
      description={!isTemplate && group.income.type ? INCOME_DOC_ENCOURAGEMENT_TEXT[group.income.type] : undefined}
    >
      {areDocumentsExpired && <Alert tone="warning">{DOCUMENTS_EXPIRED_TEXT}</Alert>}
      {group.requestedRows.map((row) => (
        <RequestedRowContainer key={row.documentType} row={row} isRequested documents={withDetails(row.documents)} />
      ))}
      <DocumentContainer
        heading={isTemplate ? SUPPORTING_DOCUMENTS_HEADING : INCOME_SOURCE_DOCUMENTS_HEADING}
        tag={isTemplate ? OPTIONAL_TAG : undefined}
        documents={withDetails(group.supportingDocuments)}
      />
    </DocumentGroup>
  );
}

function RowsGroup({
  title,
  description,
  rows,
  isRequested,
  getTag,
}: {
  title: string;
  description?: string;
  rows: Array<RequestedRow>;
  isRequested: boolean;
  getTag?: (row: RequestedRow) => string | undefined;
}) {
  const { withDetails, areDocumentsExpired } = useDocumentDetails(rows.flatMap((row) => row.documents));

  return (
    <DocumentGroup title={title} description={description}>
      {areDocumentsExpired && <Alert tone="warning">{DOCUMENTS_EXPIRED_TEXT}</Alert>}
      {rows.map((row) => (
        <RequestedRowContainer
          key={`${row.documentType}-${row.section.incomeId ?? 'application'}`}
          row={row}
          tag={getTag?.(row)}
          isRequested={isRequested}
          documents={withDetails(row.documents)}
        />
      ))}
    </DocumentGroup>
  );
}

export function RequiredDocumentsStep() {
  const { application, featureFlags, listing } = useApplicationContext();
  const isTemplateEligible = featureFlags.hasRequiredDocuments === true;
  const model = useMemo(
    () => getIncomeDocumentsModel(application.answers, { isTemplateEligible }),
    [application.answers, isTemplateEligible],
  );
  const isTemplate = model.mode === 'template';
  const requesterName = listing.listingOwner?.name?.trim() || DEFAULT_REQUESTER_NAME;
  const sourceLabels = new Map(
    model.incomeSources.flatMap(({ income, label }) => (income.id ? [[income.id, label]] : [])),
  );

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        {INCOME_DOCUMENTS_STEP_HEADING}
      </Heading>
      <Paragraph css={{ marginBottom: 'default', overflowWrap: 'anywhere' }}>
        {isTemplate ? templateModeIntro(requesterName) : DEFAULT_MODE_INTRO}
      </Paragraph>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {isTemplate && <RequirementsProgress covered={model.coveredCount} total={model.requestedCount} />}
        {model.incomeSources.length === 0 && <Alert tone="info">{NO_INCOME_SOURCES_TEXT}</Alert>}
        {model.incomeSources.map((group) => (
          <IncomeSourceDocuments key={group.income.id ?? group.index} group={group} isTemplate={isTemplate} />
        ))}
        {model.applicationRows.length > 0 && (
          <RowsGroup title={ADDITIONAL_FINANCIAL_DOCUMENTS_HEADING} rows={model.applicationRows} isRequested />
        )}
        {isTemplate && model.noLongerRequested.length > 0 && (
          <RowsGroup
            title={NO_LONGER_REQUESTED_HEADING}
            description={NO_LONGER_REQUESTED_TEXT}
            rows={model.noLongerRequested}
            isRequested={false}
            getTag={(row) => sourceLabels.get(row.section.incomeId ?? '')}
          />
        )}
      </Box>
      <Paragraph textStyle="body-sm" color="text.subtle" css={{ marginTop: 'default' }}>
        {REDACTION_TEXT} {PASSWORD_PROTECTED_TEXT}
      </Paragraph>
    </>
  );
}

export function RequiredDocumentsStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconFileFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            {STEP_TIP_HEADING}
          </Heading>
          <Paragraph>{STEP_TIP_TEXT}</Paragraph>
        </>
      }
    />
  );
}

export const RequiredDocumentsRecommendation = {
  condition: ({ answers, featureFlags }: { answers: Answers; featureFlags?: FeatureFlags }) => {
    const { coveredCount, requestedCount } = getIncomeDocumentsModel(answers, {
      isTemplateEligible: featureFlags?.hasRequiredDocuments === true,
    });
    return coveredCount < requestedCount;
  },
  title: 'Income documents',
  body: (
    <Paragraph>
      The rental manager asked for documents that your application is still missing. You can submit without them, but
      adding them helps the rental manager decide faster.
    </Paragraph>
  ),
};
